/**
 * Normalizes an Instagram handle by ensuring it starts with '@'.
 *
 * @param {string|undefined} handle - The Instagram handle to normalize
 * @returns {string|undefined} The normalized handle with '@' prefix, or undefined if input is falsy
 *
 * @example
 * normalizeInstagramHandle('username') // '@username'
 * normalizeInstagramHandle('@username') // '@username'
 * normalizeInstagramHandle(undefined) // undefined
 */
export function normalizeInstagramHandle(handle) {
  if (!handle) {
    return handle;
  }

  return handle.startsWith('@') ? handle : `@${handle}`;
}

/**
 * Resolves Instagram from addresses based on routing groups and default endpoints.
 * This function determines which Instagram address should be used for sending messages,
 * considering routing group preferences and inbox defaults.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.context - The action context with stores
 * @param {Array<{handle: string, id: string}>} params.allFromAddresses - All available Instagram addresses
 * @param {Object} params.conversation - The conversation object
 * @param {Array<Object>} params.endpoints - The Instagram endpoints
 * @param {string} params.endpointType - The message channel type (e.g., INSTAGRAM_DIRECT, INSTAGRAM_COMMENT)
 * @param {string} params.routingGroupId - The routing group ID
 * @returns {{default: {handle: string, id: string}, preferred: {handle: string, id: string}, allFromAddresses: Array}}
 */
export function getInstagramFromAddresses({
  context,
  allFromAddresses,
  conversation,
  endpoints,
  endpointType,
  routingGroupId,
}) {
  const firstRoutingGroupNumber = context.stores.communicationQueues
    .findAll()
    .filter(commQueue => commQueue.routingGroupId === routingGroupId)
    .map(commQueue => endpoints.find(endpoint => endpoint.id === commQueue.endpointId))
    .filter(Boolean)
    .map(endpoint => ({
      handle: endpoint.configuration.handle,
      id: endpoint.address,
    }))[0];

  const channelConfiguration = context.stores.channelConfiguration.get();
  const currentInbox = context.stores.routingGroups.findBy({ id: conversation.assignee.routingGroupId });
  const defaultEndpointId = currentInbox && currentInbox.defaultEndpoints[endpointType];
  const defaultEndpoint = channelConfiguration && channelConfiguration.endpoints.find(e => e.id === defaultEndpointId);
  const defaultAddress = defaultEndpoint
    ? {
        handle: defaultEndpoint.configuration.handle,
        id: defaultEndpoint.address,
      }
    : undefined;

  return {
    default: defaultAddress || firstRoutingGroupNumber || allFromAddresses[0],
    preferred: firstRoutingGroupNumber,
    allFromAddresses,
  };
}

/**
 * Selects the company address and handle from potential addresses.
 * Attempts to match the company address from the conversation item,
 * falling back to the default if no match is found.
 *
 * @param {Object} potentialFroms - Object containing potential from addresses
 * @param {Object} potentialFroms.default - The default address object
 * @param {Array<{handle: string, id: string}>} potentialFroms.allFromAddresses - All available addresses
 * @param {Object} conversationItem - The conversation item
 * @param {Object} conversationItem.content - The conversation item content
 * @param {string} conversationItem.content.companyAddress - The company address from the item
 * @returns {{companyAddress: string, companyHandle: string}} The selected company address and handle
 */
export function selectCompanyAddress(potentialFroms, conversationItem) {
  const matchedAddress = potentialFroms.allFromAddresses.find(
    address => address.id === conversationItem.content.companyAddress
  );
  const selectedCompany = matchedAddress || potentialFroms?.default;
  const companyAddress = selectedCompany?.id;
  const companyHandle = selectedCompany?.handle;

  return { companyAddress, companyHandle };
}

/**
 * Finds the display name for a customer's Instagram address from their profile.
 * Searches through all Instagram addresses (both Direct and Comment types) to find a match.
 *
 * @param {Object} profile - The customer profile object
 * @param {string} customerAddress - The customer address ID to find
 * @returns {string|undefined} The display name if found, undefined otherwise
 */
export function findCustomerInstagramDisplayName(profile, customerAddress) {
  if (!profile || !customerAddress) {
    return undefined;
  }

  const instagramAddresses = profile.getCustomerInstagramAddresses();
  const matchedCustomerInstagramAddress = instagramAddresses.find(address => address.id === customerAddress);
  return matchedCustomerInstagramAddress?.displayName;
}

/**
 * Checks if an Instagram composition can be created based on role features and channel configuration.
 *
 * @param {Object} context - The action context with stores
 * @param {Function} addressCheckFn - Function that takes channelConfiguration and returns array of addresses to check
 * @returns {boolean} True if composition can be created, false otherwise
 *
 * @example
 * canCreateInstagramComposition(context, (config) => config.companyInstagramAddresses())
 * canCreateInstagramComposition(context, (config) => config.companyInstagramCommentAddresses())
 */
export function canCreateInstagramComposition(context, addressCheckFn) {
  const hasConversationOutboundPermission = context.stores.appFeatures.get().isEnabled('conversation:outbound');
  if (!hasConversationOutboundPermission) {
    return false;
  }

  const channelConfiguration = context.stores.channelConfiguration.get();
  if (!channelConfiguration) {
    return false;
  }

  const addresses = addressCheckFn(channelConfiguration);
  return (addresses || []).length > 0;
}
